import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import SecurityIcon from '@mui/icons-material/Security';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import PhoneIcon from '@mui/icons-material/Phone';
import { useNavigate } from 'react-router-dom';
import DashboardBarChart from '../../components/charts/DashboardBarChart';
import WipStatusCard from '../../components/Dashboard/WipStatusCard';
import DateRangeExportBar from '../../components/Dashboard/DateRangeExportBar';
import OdmTypeFilter from '../../components/Dashboard/OdmTypeFilter';
import YearQuarterFilter from '../../components/Dashboard/YearQuarterFilter';
import ChartDetailModal from '../../components/Dashboard/ChartDetailModal';
import DashboardAnalyticsService from '../../services/DashboardAnalyticsService';

const ALLIANZ_BLUE = '#003781';

const GRAPH1C_STATUS_OPTIONS = [
  { key: 'new',       label: 'New',       color: '#1976D2' },
  { key: 'wip',       label: 'WIP',       color: '#0057A8' },
  { key: 'completed', label: 'Completed', color: '#003781' },
];

const GRAPH3D_STATUS_OPTIONS = [
  { key: 'new',       label: 'New',       field: 'new_count',       color: '#1976D2' },
  { key: 'wip',       label: 'WIP',       field: 'wip_count',       color: '#0057A8' },
  { key: 'completed', label: 'Completed', field: 'completed_count', color: '#003781' },
];

// Known WIP card types with accent colors and icons
const WIP_CARDS = [
  { label: 'Total WIP',  requestType: 'all',                                accentColor: '#003781', icon: <DashboardIcon /> },
  { label: 'BW WIP',     requestType: 'Bandwidth Upgrade/Downgrade',         accentColor: '#0057A8', icon: <NetworkCheckIcon /> },
  { label: 'FW WIP',     requestType: 'AGN Managed-Firewall Decommissioning',accentColor: '#1976D2', icon: <SecurityIcon /> },
  { label: 'WX WIP',     requestType: 'AGN Webex Decommissioning',           accentColor: '#1565C0', icon: <VideoCallIcon /> },
  { label: 'IPT WIP',    requestType: 'AGN IPT Decommissioning',             accentColor: '#42A5F5', icon: <PhoneIcon /> },
];

function pivotByRequestStatus(data) {
  const map = {};
  data.forEach(({ quarter, request_status, count }) => {
    if (!map[quarter]) map[quarter] = { quarter };
    map[quarter][request_status] = (map[quarter][request_status] || 0) + count;
  });
  return Object.values(map).sort((a, b) => a.quarter.localeCompare(b.quarter));
}

function pivotByApprovalStatus(data) {
  const map = {};
  data.forEach(({ quarter, approval_status, count }) => {
    if (!map[quarter]) map[quarter] = { quarter };
    map[quarter][approval_status] = (map[quarter][approval_status] || 0) + count;
  });
  return Object.values(map).sort((a, b) => a.quarter.localeCompare(b.quarter));
}

function pivotVolumeByType(data) {
  const map = {};
  data.forEach(({ request_type, year, total }) => {
    if (!map[request_type]) map[request_type] = { request_type };
    map[request_type][year] = (map[request_type][year] || 0) + total;
  });
  return Object.values(map);
}

const SectionHeader = ({ title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
    <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: ALLIANZ_BLUE }} />
    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a2e' }}>
      {title}
    </Typography>
  </Box>
);

const AnalyticsDashboardContent = () => {
  const navigate = useNavigate();
  const currentYear = String(new Date().getFullYear());

  const [filters, setFilters] = useState({ odmType: '', year: '', quarter: '' });
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [odmTypeOptions, setOdmTypeOptions] = useState([]);
  const [graph1cFilters, setGraph1cFilters] = useState(new Set(['new', 'wip', 'completed']));

  const toggleGraph1cFilter = (key) => {
    setGraph1cFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const clearGraph1cFilters = () => setGraph1cFilters(new Set(['new', 'wip', 'completed']));

  const [graph3dFilters, setGraph3dFilters] = useState(new Set(['new', 'wip', 'completed']));

  const toggleGraph3dFilter = (key) => {
    setGraph3dFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const clearGraph3dFilters = () => setGraph3dFilters(new Set(['new', 'wip', 'completed']));

  const [graph1aData, setGraph1aData] = useState([]);
  const [graph1bData, setGraph1bData] = useState([]);
  const [graph2Data, setGraph2Data] = useState([]);
  const [wipData, setWipData] = useState({ total_wip: 0, by_type: [] });
  const [wipTotals, setWipTotals] = useState({ total_wip: 0, by_year: [] });
  const [loadingWipTotals, setLoadingWipTotals] = useState(false);
  const [rawGraph1a, setRawGraph1a] = useState([]);
  const [rawGraph1b, setRawGraph1b] = useState([]);
  const [rawGraph2, setRawGraph2] = useState([]);

  const [typeYears, setTypeYears] = useState([currentYear]);

  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingGraph1, setLoadingGraph1] = useState(false);
  const [loadingGraph2, setLoadingGraph2] = useState(false);
  const [loadingWip, setLoadingWip] = useState(false);

  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  const openModal = (config) => {
    setActiveModal(config);
    setModalOpen(true);
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoadingFilters(true);
        const opts = await DashboardAnalyticsService.getFilterOptions();
        const HIDDEN_ODM_TYPES = [
          'AGN IPT Decommissioning Request',
          'AGN Webex Decommissioning',
          'AGN Managed-Firewall Decommissioning',
        ];
        setOdmTypeOptions((opts.odm_types || []).filter(t => !HIDDEN_ODM_TYPES.includes(t)));
        const yrs = opts.years && opts.years.length > 0 ? opts.years : [currentYear];
        setAvailableYears(yrs);
      } catch (err) {
        console.error('Failed to load filter options', err);
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGraph1 = useCallback(async () => {
    setLoadingGraph1(true);
    setError('');
    try {
      const params = {
        year: filters.year,
        quarter: filters.quarter,
        odm_type: filters.odmType,
      };
      const [statusData, approvalData] = await Promise.all([
        DashboardAnalyticsService.getVolumeByRequestStatus(params),
        DashboardAnalyticsService.getVolumeByApprovalStatus(params),
      ]);
      setRawGraph1a(statusData);
      setRawGraph1b(approvalData);
      setGraph1aData(pivotByRequestStatus(statusData));
      setGraph1bData(pivotByApprovalStatus(approvalData));
    } catch (err) {
      console.error('Graph 1 fetch failed', err);
      setError('Failed to load volume by status data.');
    } finally {
      setLoadingGraph1(false);
    }
  }, [filters.year, filters.quarter, filters.odmType]);

  const fetchGraph2 = useCallback(async () => {
    setLoadingGraph2(true);
    try {
      const rawData = await DashboardAnalyticsService.getVolumeByType({
        years: [filters.year],
        include_wip: 'true',
        odm_type: filters.odmType,
      });
      setRawGraph2(rawData);

      const years = [...new Set(rawData.map((d) => d.year))].sort();
      setTypeYears(years);

      setGraph2Data(pivotVolumeByType(rawData));
    } catch (err) {
      console.error('Graph 2 fetch failed', err);
    } finally {
      setLoadingGraph2(false);
    }
  }, [filters.year, filters.odmType]);

  const fetchWip = useCallback(async () => {
    setLoadingWip(true);
    try {
      const data = await DashboardAnalyticsService.getWipSummary({
        odm_type: filters.odmType,
        year: filters.year,
      });
      setWipData(data);
    } catch (err) {
      console.error('WIP fetch failed', err);
    } finally {
      setLoadingWip(false);
    }
  }, [filters.odmType, filters.year]);

  const fetchWipTotals = useCallback(async () => {
    setLoadingWipTotals(true);
    try {
      const data = await DashboardAnalyticsService.getWipTotals({
        odm_type: filters.odmType,
        years: ['2024', '2025', '2026'],
      });
      setWipTotals(data);
    } catch (err) {
      console.error('WIP totals fetch failed', err);
    } finally {
      setLoadingWipTotals(false);
    }
  }, [filters.odmType]);

  useEffect(() => { fetchGraph1(); }, [fetchGraph1]);
  useEffect(() => { fetchGraph2(); }, [fetchGraph2]);
  useEffect(() => { fetchWip(); }, [fetchWip]);
  useEffect(() => { fetchWipTotals(); }, [fetchWipTotals]);

  const handleExport1a = (startDate, endDate, format) => {
    DashboardAnalyticsService.downloadExport(rawGraph1a, 'volume-by-request-status', format);
  };

  const handleExport1b = (startDate, endDate, format) => {
    DashboardAnalyticsService.downloadExport(rawGraph1b, 'volume-by-approval-status', format);
  };

  const getWipCountForType = (requestType) => {
    if (requestType === 'all') return wipData.total_wip || 0;
    const entry = (wipData.by_type || []).find((t) => t.request_type === requestType);
    return entry ? entry.count : 0;
  };

  const handleRefresh = () => {
    fetchGraph1();
    fetchGraph2();
    fetchWip();
    fetchWipTotals();
  };

  const graph1aSeries = [
    { dataKey: 'New', name: 'New', color: '#1976D2' },
    { dataKey: 'Work in Progress', name: 'Work in Progress', color: '#0057A8' },
    { dataKey: 'Completed', name: 'Completed', color: '#003781' },
  ];

  const graph1bSeries = [
    { dataKey: 'Pending', name: 'Pending Approval', color: '#1976D2' },
    { dataKey: 'Approved', name: 'Approved', color: '#003781' },
    { dataKey: 'Not Applicable', name: 'Not Applicable', color: '#90CAF9' },
  ];

  const graph2Series = typeYears.map((y, idx) => ({
    dataKey: y,
    name: y,
    color: ['#003781', '#0057A8', '#1976D2', '#42A5F5'][idx % 4],
  }));

  const graph1cData = useMemo(() => {
    const map = {};
    rawGraph2.forEach(({ request_type, year, new_count, wip_count, completed_count }) => {
      if (!map[request_type]) map[request_type] = { request_type };
      let total = 0;
      if (graph1cFilters.has('new'))       total += new_count || 0;
      if (graph1cFilters.has('wip'))       total += wip_count || 0;
      if (graph1cFilters.has('completed')) total += completed_count || 0;
      map[request_type][year] = (map[request_type][year] || 0) + total;
    });
    return Object.values(map);
  }, [rawGraph2, graph1cFilters]);

  const graph1cSeries = typeYears.map((y, idx) => ({
    dataKey: y,
    name: y,
    color: ['#003781', '#0057A8', '#1976D2', '#42A5F5'][idx % 4],
  }));

  const graph3dData = useMemo(() => {
    const map = {};
    rawGraph2.forEach(({ request_type, year, new_count, wip_count, completed_count, total }) => {
      if (!map[request_type]) map[request_type] = { request_type };
      const selected = (graph3dFilters.has('new') ? (new_count || 0) : 0)
                     + (graph3dFilters.has('wip') ? (wip_count || 0) : 0)
                     + (graph3dFilters.has('completed') ? (completed_count || 0) : 0);
      const pct = total > 0 ? parseFloat(((selected / total) * 100).toFixed(1)) : 0;
      map[request_type][year] = (map[request_type][year] || 0) + pct;
    });
    return Object.values(map);
  }, [rawGraph2, graph3dFilters]);

  const graph3dSeries = typeYears.map((y, idx) => ({
    dataKey: y,
    name: y,
    color: ['#003781', '#0057A8', '#1976D2', '#42A5F5'][idx % 4],
  }));

  const chartPaperSx = {
    borderRadius: '16px',
    border: '1px solid #d6e4f7',
    boxShadow: '0 2px 16px rgba(0,55,129,0.07)',
    p: 2.5,
    background: '#f5f8fd',
  };

  return (
    <Box p={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ---- Global filter bar ---- */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: 'none',
          background: '#f0f6ff',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <OdmTypeFilter
            value={filters.odmType}
            onChange={(v) => setFilters((f) => ({ ...f, odmType: v }))}
            options={odmTypeOptions}
            loading={loadingFilters}
          />
          <YearQuarterFilter
            year={filters.year}
            onYearChange={(v) => setFilters((f) => ({ ...f, year: v }))}
            quarter={filters.quarter}
            onQuarterChange={(v) => setFilters((f) => ({ ...f, quarter: v }))}
            years={availableYears}
          />
          <Tooltip title="Refresh all graphs">
            <IconButton onClick={handleRefresh} size="small" sx={{ color: ALLIANZ_BLUE }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* ================================================================
          Section 1: Volume of ODMs per Status
      ================================================================ */}
      <SectionHeader title="Volume of ODMs per Status" />
      <Typography variant="body2" color="text.secondary" mb={2}>
        Grouped by quarter
      </Typography>

      <Grid container spacing={3} mb={4}>
        {/* Graph 1a */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={chartPaperSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
                Graph 1a — By Request Status
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openModal({
                  title: 'Graph 1a — Volume by Request Status',
                  subtitle: 'Quarterly breakdown of ODM requests grouped by request status',
                  accentColor: '#1976D2',
                  chartType: 'bar',
                  chartData: graph1aData,
                  xKey: 'quarter',
                  series: graph1aSeries,
                  tableData: rawGraph1a,
                  tableColumns: [
                    { key: 'year', label: 'Year' },
                    { key: 'quarter', label: 'Quarter' },
                    { key: 'request_status', label: 'Request Status' },
                    { key: 'count', label: 'Count' },
                  ],
                  rawData: rawGraph1a,
                  exportName: 'volume-by-request-status',
                  pivotData: rawGraph1a,
                  pivotStatusKey: 'request_status',
                })}
                sx={{ textTransform: 'none', fontSize: 12, color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE }}
              >
                Data View
              </Button>
            </Stack>
            <Divider sx={{ mt: 1.5, mb: 2, borderColor: '#f0f1f3' }} />
            <DashboardBarChart
              data={graph1aData}
              xKey="quarter"
              series={graph1aSeries}
              height={280}
              loading={loadingGraph1}
            />
            <DateRangeExportBar
              title="Graph 1a"
              onExport={handleExport1a}
              loading={loadingGraph1}
              year={filters.year}
            />
          </Paper>
        </Grid>

        {/* Graph 1b */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={chartPaperSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
                Graph 1b — By Approval Status
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openModal({
                  title: 'Graph 1b — Volume by Approval Status',
                  subtitle: 'Quarterly breakdown of ODM requests grouped by approval status',
                  accentColor: '#1976D2',
                  chartType: 'bar',
                  chartData: graph1bData,
                  xKey: 'quarter',
                  series: graph1bSeries,
                  tableData: rawGraph1b,
                  tableColumns: [
                    { key: 'year', label: 'Year' },
                    { key: 'quarter', label: 'Quarter' },
                    { key: 'approval_status', label: 'Approval Status' },
                    { key: 'count', label: 'Count' },
                  ],
                  rawData: rawGraph1b,
                  exportName: 'volume-by-approval-status',
                  pivotData: rawGraph1b,
                  pivotStatusKey: 'approval_status',
                })}
                sx={{ textTransform: 'none', fontSize: 12, color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE }}
              >
                Data View
              </Button>
            </Stack>
            <Divider sx={{ mt: 1.5, mb: 2, borderColor: '#f0f1f3' }} />
            <DashboardBarChart
              data={graph1bData}
              xKey="quarter"
              series={graph1bSeries}
              height={280}
              loading={loadingGraph1}
            />
            <DateRangeExportBar
              title="Graph 1b"
              onExport={handleExport1b}
              loading={loadingGraph1}
              year={filters.year}
            />
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 4 }} />

      {/* ================================================================
          Section 1c: Volume of New ODMs per Type within a Year
      ================================================================ */}
      <SectionHeader title="Volume of New ODMs per Type within a Year" />
      <Typography variant="body2" color="text.secondary" mb={2}>
        Grouped by ODM type — one bar per year
      </Typography>

      <Paper elevation={0} sx={{ ...chartPaperSx, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
            Graph 1c — By ODM Type per Year
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openModal({
              title: 'Graph 1c — Volume by ODM Type per Year',
              subtitle: 'Total ODM requests per type, grouped by year',
              accentColor: '#003781',
              chartType: 'bar',
              chartData: graph1cData,
              xKey: 'request_type',
              series: graph1cSeries,
              tableData: rawGraph2,
              tableColumns: [],
              rawData: rawGraph2,
              exportName: 'volume-by-type-per-year',
              pivotType: 'volume',
              pivotStatusKeys: [...graph1cFilters].map((k) =>
                k === 'new' ? 'new_count' : k === 'wip' ? 'wip_count' : 'completed_count'
              ),
            })}
            sx={{ textTransform: 'none', fontSize: 12, color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE }}
          >
            Data View
          </Button>
        </Stack>
        <Divider sx={{ mt: 1.5, mb: 2, borderColor: '#f0f1f3' }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DashboardBarChart
              data={graph1cData}
              xKey="request_type"
              series={graph1cSeries}
              height={320}
              loading={loadingGraph2}
              yTickInterval={10}
            />
          </Box>

          <Box
            sx={{
              width: 140,
              flexShrink: 0,
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              p: 1.5,
              background: '#f0f6ff',
              mt: 1,
            }}
          >
            <Typography variant="caption" fontWeight={700} sx={{ color: '#374151', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Status
            </Typography>
            <Stack spacing={0.75}>
              {GRAPH1C_STATUS_OPTIONS.map((opt) => {
                const active = graph1cFilters.has(opt.key);
                return (
                  <Chip
                    key={opt.key}
                    label={opt.label}
                    size="small"
                    onClick={() => toggleGraph1cFilter(opt.key)}
                    sx={{
                      justifyContent: 'flex-start',
                      fontWeight: active ? 700 : 400,
                      fontSize: 12,
                      bgcolor: active ? `${opt.color}18` : '#f3f4f6',
                      color: active ? opt.color : '#9ca3af',
                      border: `1.5px solid ${active ? opt.color : 'transparent'}`,
                      cursor: 'pointer',
                      '& .MuiChip-label': { px: 1 },
                      '&:hover': { bgcolor: `${opt.color}28` },
                      transition: 'all 0.15s',
                    }}
                    icon={
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: active ? opt.color : '#d1d5db',
                        ml: '6px !important', mr: '-2px',
                        flexShrink: 0,
                      }} />
                    }
                  />
                );
              })}
            </Stack>
            {graph1cFilters.size < GRAPH1C_STATUS_OPTIONS.length && (
              <Button
                size="small"
                onClick={clearGraph1cFilters}
                sx={{
                  mt: 1.5, p: 0, minWidth: 0, fontSize: 11,
                  textTransform: 'none', color: ALLIANZ_BLUE,
                  '&:hover': { background: 'none', textDecoration: 'underline' },
                }}
              >
                Show all
              </Button>
            )}
          </Box>
        </Box>

        <DateRangeExportBar
          title="Graph 1c"
          onExport={(s, e, fmt) => DashboardAnalyticsService.downloadExport(rawGraph2, 'volume-by-type-per-year', fmt)}
          loading={loadingGraph2}
          year={filters.year}
        />
      </Paper>

      <Divider sx={{ mb: 4 }} />

      {/* ================================================================
          Section 1d: Ratio of New ODMs per Type within a Year
      ================================================================ */}
      <SectionHeader title="Ratio of New ODMs per Type within a Year" />
      <Typography variant="body2" color="text.secondary" mb={2}>
        % of selected statuses per ODM type — one bar per year
      </Typography>

      <Paper elevation={0} sx={{ ...chartPaperSx, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
            Graph 1d — Ratio by ODM Type per Year
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openModal({
              title: 'Graph 1d — Ratio by ODM Type per Year',
              subtitle: '% of selected statuses per request type, grouped by year',
              accentColor: '#0057A8',
              chartType: 'bar',
              chartData: graph3dData,
              xKey: 'request_type',
              series: graph3dSeries,
              tableData: rawGraph2,
              tableColumns: [],
              rawData: rawGraph2,
              exportName: 'ratio-by-type-per-year',
              pivotType: 'ratio',
              pivotStatusKeys: [...graph3dFilters].map((k) =>
                k === 'new' ? 'new_count' : k === 'wip' ? 'wip_count' : 'completed_count'
              ),
            })}
            sx={{ textTransform: 'none', fontSize: 12, color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE }}
          >
            Data View
          </Button>
        </Stack>
        <Divider sx={{ mt: 1.5, mb: 2, borderColor: '#f0f1f3' }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DashboardBarChart
              data={graph3dData}
              xKey="request_type"
              series={graph3dSeries}
              height={320}
              loading={loadingGraph2}
              yTickInterval={10}
              yTickFormatter={(v) => `${v}%`}
            />
          </Box>

          <Box
            sx={{
              width: 140,
              flexShrink: 0,
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              p: 1.5,
              background: '#f0f6ff',
              mt: 1,
            }}
          >
            <Typography variant="caption" fontWeight={700} sx={{ color: '#374151', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Status
            </Typography>
            <Stack spacing={0.75}>
              {GRAPH3D_STATUS_OPTIONS.map((opt) => {
                const active = graph3dFilters.has(opt.key);
                return (
                  <Chip
                    key={opt.key}
                    label={opt.label}
                    size="small"
                    onClick={() => toggleGraph3dFilter(opt.key)}
                    sx={{
                      justifyContent: 'flex-start',
                      fontWeight: active ? 700 : 400,
                      fontSize: 12,
                      bgcolor: active ? `${opt.color}18` : '#f3f4f6',
                      color: active ? opt.color : '#9ca3af',
                      border: `1.5px solid ${active ? opt.color : 'transparent'}`,
                      cursor: 'pointer',
                      '& .MuiChip-label': { px: 1 },
                      '&:hover': { bgcolor: `${opt.color}28` },
                      transition: 'all 0.15s',
                    }}
                    icon={
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: active ? opt.color : '#d1d5db',
                        ml: '6px !important', mr: '-2px',
                        flexShrink: 0,
                      }} />
                    }
                  />
                );
              })}
            </Stack>
            {graph3dFilters.size < GRAPH3D_STATUS_OPTIONS.length && (
              <Button
                size="small"
                onClick={clearGraph3dFilters}
                sx={{
                  mt: 1.5, p: 0, minWidth: 0, fontSize: 11,
                  textTransform: 'none', color: ALLIANZ_BLUE,
                  '&:hover': { background: 'none', textDecoration: 'underline' },
                }}
              >
                Show all
              </Button>
            )}
          </Box>
        </Box>

        <DateRangeExportBar
          title="Graph 1d"
          onExport={(s, e, fmt) => DashboardAnalyticsService.downloadExport(rawGraph2, 'ratio-by-type-per-year', fmt)}
          loading={loadingGraph2}
          year={filters.year}
        />
      </Paper>

      <Divider sx={{ mb: 4 }} />

      {/* ================================================================
          Section 2a: Total WIP Summary Tiles
      ================================================================ */}
      <SectionHeader title="Total Work in Progress ODMs" />

      <Stack direction="row" spacing={2} mb={4} flexWrap="wrap" useFlexGap>
        {/* All-time total tile */}
        <Box
          onClick={() => navigate('/track-requests?request_status=Work+in+Progress')}
          sx={{
            flex: 1, minWidth: 160,
            background: 'linear-gradient(135deg, #003781 0%, #0057A8 100%)',
            borderRadius: '14px',
            p: 2.5,
            color: '#fff',
            boxShadow: '0 4px 16px rgba(0,55,129,0.18)',
            cursor: 'pointer',
            transition: 'transform 0.18s, box-shadow 0.18s',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 8px 28px rgba(0,55,129,0.28)',
            },
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
            All Time
          </Typography>
          {loadingWipTotals ? (
            <CircularProgress size={28} sx={{ color: '#fff', mt: 1, display: 'block' }} />
          ) : (
            <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1, fontSize: '2.4rem' }}>
              {wipTotals.total_wip}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.85, fontWeight: 500 }}>
            Total WIP ODMs
          </Typography>
        </Box>

        {/* Per-year tiles */}
        {(wipTotals.by_year || []).map((item, idx) => {
          const yearColors = ['#0057A8', '#1976D2', '#42A5F5'];
          const color = yearColors[idx % yearColors.length];
          return (
            <Box
              key={item.year}
              onClick={() => navigate('/track-requests?request_status=Work+in+Progress')}
              sx={{
                flex: 1, minWidth: 160,
                background: 'linear-gradient(135deg, #EBF2FB 0%, #f5f8fd 100%)',
                borderRadius: '14px',
                p: 2.5,
                borderTop: `4px solid ${color}`,
                boxShadow: '0 2px 16px rgba(0,55,129,0.10)',
                cursor: 'pointer',
                transition: 'transform 0.18s, box-shadow 0.18s',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
                },
              }}
            >
              <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                Since {item.year}
              </Typography>
              {loadingWipTotals ? (
                <CircularProgress size={28} sx={{ color, mt: 1, display: 'block' }} />
              ) : (
                <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1, fontSize: '2.4rem', color }}>
                  {item.count}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1, color: '#6b7280', fontWeight: 500 }}>
                WIP ODMs in {item.year}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Divider sx={{ mb: 4 }} />

      {/* ================================================================
          Section 2: Work in Progress Cards
      ================================================================ */}
      <SectionHeader title="Work in Progress" />

      <Box
        sx={{
          background: 'linear-gradient(135deg, #EBF2FB 0%, #f5f8fd 100%)',
          borderRadius: '16px',
          p: 3,
          mb: 0,
        }}
      >
        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
          {WIP_CARDS.map((card) => {
            const cardTableData = card.requestType === 'all'
              ? (wipData.by_type || [])
              : (wipData.by_type || []).filter((r) => r.request_type === card.requestType);
            return (
              <WipStatusCard
                key={card.requestType}
                label={card.label}
                count={getWipCountForType(card.requestType)}
                requestType={card.requestType}
                year={filters.year}
                loading={loadingWip}
                accentColor={card.accentColor}
                icon={card.icon}
                onViewAll={() => openModal({
                  title: `${card.label} — Work in Progress`,
                  subtitle: card.requestType === 'all'
                    ? 'WIP count across all ODM request types'
                    : `WIP count for ${card.requestType}`,
                  accentColor: card.accentColor,
                  chartType: 'bar',
                  chartData: card.requestType === 'all'
                    ? (wipData.by_type || [])
                    : cardTableData,
                  xKey: 'request_type',
                  series: [{ dataKey: 'count', name: 'WIP Count', color: card.accentColor }],
                  tableData: cardTableData,
                  tableColumns: [
                    { key: 'request_type', label: 'Request Type' },
                    { key: 'count', label: 'WIP Count' },
                  ],
                  rawData: cardTableData,
                  exportName: `wip-${card.requestType === 'all' ? 'summary' : card.requestType.replace(/\s+/g, '-').toLowerCase()}`,
                })}
              />
            );
          })}
        </Stack>
      </Box>

      {/* ── Chart detail modal ── */}
      {activeModal && (
        <ChartDetailModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={activeModal.title}
          subtitle={activeModal.subtitle}
          accentColor={activeModal.accentColor}
          chartType={activeModal.chartType}
          chartData={activeModal.chartData}
          xKey={activeModal.xKey}
          series={activeModal.series}
          tableData={activeModal.tableData}
          tableColumns={activeModal.tableColumns}
          rawData={activeModal.rawData}
          exportName={activeModal.exportName}
          pivotData={activeModal.pivotData || null}
          pivotStatusKey={activeModal.pivotStatusKey || 'request_status'}
          pivotType={activeModal.pivotType || null}
          pivotStatusKeys={activeModal.pivotStatusKeys || ['new_count', 'wip_count', 'completed_count']}
        />
      )}
    </Box>
  );
};

export default AnalyticsDashboardContent;
